import type { SymbolSnapshot } from '../types/market';

export interface SnapshotRow {
  symbol: string;
  price?: number;
  changePct?: number;
  signal?: 'BUY' | 'SELL' | 'HOLD';
  confidence?: number;
  score?: number;
  state?: string;
  rsi?: number;
  volumeRatio?: number;
  latencyMs?: number;
  updatedAt?: number;
  pmPrice?: number;
  pmChangePct?: number;
  pmVolume?: number;
}

const nyDateFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', dateStyle: 'short' });
const nyTimeFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', timeStyle: 'short', hour12: false });

export function snapshotToRow(snapshot: SymbolSnapshot): SnapshotRow {
  const candles = snapshot.data?.candles;
  const latest = candles?.at(-1);
  const previousClose = snapshot.data?.meta.previousClose ?? candles?.at(-2)?.close;
  const changePct = latest && previousClose && previousClose !== 0
    ? ((latest.close - previousClose) / previousClose) * 100
    : undefined;

  let pmPrice: number | undefined;
  let pmVolume: number | undefined;
  let pmChangePct: number | undefined;

  const interval = snapshot.data?.interval;
  if (candles && candles.length > 0 && interval && ['1m', '5m', '15m', '1h'].includes(interval)) {
    const latestDate = new Date(candles[candles.length - 1].time * 1000);
    const targetDayStr = nyDateFormatter.format(latestDate);
    let volAccum = 0;

    for (let i = candles.length - 1; i >= 0; i--) {
      const c = candles[i];
      const d = new Date(c.time * 1000);
      const dayStr = nyDateFormatter.format(d);
      
      if (dayStr !== targetDayStr) {
        if (candles.length > 100) break;
      }
      
      if (dayStr === targetDayStr) {
        const timeStr = nyTimeFormatter.format(d);
        const [hStr, mStr] = timeStr.split(':');
        let h = parseInt(hStr, 10);
        if (h === 24) h = 0;
        const m = parseInt(mStr || '0', 10);
        const timeFloat = h + (m / 60);
        
        if (timeFloat >= 4 && timeFloat < 9.5) {
          if (pmPrice === undefined) pmPrice = c.close;
          volAccum += c.volume;
        }
      }
    }
    if (pmPrice !== undefined) {
      pmVolume = volAccum;
      pmChangePct = previousClose && previousClose !== 0 
        ? ((pmPrice - previousClose) / previousClose) * 100 
        : undefined;
    }
  }

  return {
    symbol: snapshot.symbol,
    price: latest?.close,
    changePct,
    signal: snapshot.analysis?.signal,
    confidence: snapshot.analysis?.confidence,
    score: snapshot.analysis?.score,
    state: snapshot.analysis?.market_state,
    rsi: snapshot.analysis?.rsi14,
    volumeRatio: snapshot.analysis?.volume_ratio20,
    latencyMs: snapshot.data?.latencyMs,
    updatedAt: snapshot.updatedAt,
    pmPrice,
    pmChangePct,
    pmVolume
  };
}

export function downloadScannerCsv(rows: SnapshotRow[]): void {
  const header = ['Symbol', 'Price', 'Change %', 'PM Change %', 'PM Vol', 'Signal', 'Confidence %', 'Score', 'Market State', 'RSI14', 'Volume Ratio', 'Latency ms'];
  const body = rows.map((row) => [
    row.symbol,
    numberOrBlank(row.price, 4),
    numberOrBlank(row.changePct, 3),
    numberOrBlank(row.pmChangePct, 3),
    row.pmVolume != null ? String(row.pmVolume) : '',
    row.signal ?? '',
    row.confidence == null ? '' : (row.confidence * 100).toFixed(1),
    numberOrBlank(row.score, 3),
    row.state ?? '',
    numberOrBlank(row.rsi, 2),
    numberOrBlank(row.volumeRatio, 2),
    row.latencyMs ?? ''
  ]);
  const csv = [header, ...body].map((row) => row.map(csvCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `goldmine-vision-scanner-${new Date().toISOString().slice(0, 10)}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function numberOrBlank(value: number | undefined, digits: number): string {
  return value == null || !Number.isFinite(value) ? '' : value.toFixed(digits);
}

function csvCell(value: unknown): string {
  const text = String(value ?? '');
  return `"${text.replaceAll('"', '""')}"`;
}
